// ANY EDITS TO THIS PAGE SHOULD ALSO BE MADE TO ITS DEMO COUNTERPART, TO KEEP THE APP AND DEMO IN SYNC
import './TryoutAthletesPage.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getFirestore } from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import AthleteCard from '../components/common/AthleteCard/AthleteCard';
import AthleteQuickSelectTabBar from '../components/common/AthleteQuickSelectTabBar/AthleteQuickSelectTabBar';
import AthleteSearchBar from '../components/common/AthleteSearchBar/AthleteSearchBar';
import Button from '../components/common/Button';
import PageTopNav from '../components/common/PageTopNav/PageTopNav';
import ScrollToTopFab from '../components/common/ScrollToTopFab/ScrollToTopFab';
import { useScoringAuth } from '../contexts/ScoringAuthContext';
import { scoringApp } from '../firebase';
import {
  fetchTryoutForScoring,
  TryoutScoringData,
} from '../services/athleteService';
import {
  subscribeTryoutCoach,
  TryoutCoach,
} from '../services/coachService';
import {
  AthletePosition,
  AthleteWithId,
} from '../types/athlete';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: TryoutScoringData };

type FilterValue = 'all' | AthletePosition;

const SCROLL_TOP_FAB_THRESHOLD_PX = 180;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TryoutAthletesPage: React.FC = () => {
  const { tryoutId } = useParams<{ tryoutId: string }>();
  const { scoringUser } = useScoringAuth();
  const navigate = useNavigate();
  const db = getFirestore(scoringApp);

  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterValue>('all');
  const [coach, setCoach] = useState<TryoutCoach | null>(null);
  const [scrollTopFabVisible, setScrollTopFabVisible] = useState(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearchQuery('');
    setActiveFilter('all');
    setScrollTopFabVisible(false);
  }, [tryoutId]);

  const handleContentScroll = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    const next = el.scrollTop > SCROLL_TOP_FAB_THRESHOLD_PX;
    setScrollTopFabVisible((prev) => (prev === next ? prev : next));
  }, []);

  const scrollContentToTop = useCallback((): void => {
    const el = contentScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!tryoutId) {
      setLoadState({ status: 'error', message: 'Invalid tryout link.' });
      return;
    }

    let cancelled = false;
    setLoadState({ status: 'loading' });

    fetchTryoutForScoring(db, tryoutId)
      .then((data) => {
        if (!cancelled) setLoadState({ status: 'ready', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load athletes.';
          setLoadState({ status: 'error', message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [db, tryoutId]);

  useEffect(() => {
    if (!tryoutId || !scoringUser) {
      setCoach(null);
      return;
    }
    return subscribeTryoutCoach(
      db,
      tryoutId,
      scoringUser.uid,
      (nextCoach) => setCoach(nextCoach),
      () => setCoach(null),
    );
  }, [db, tryoutId, scoringUser]);

  const sortedAthletes = useMemo<AthleteWithId[]>(() => {
    if (loadState.status !== 'ready') return [];
    return [...loadState.data.athletes].sort((a, b) => {
      const na = parseInt(a.number, 10);
      const nb = parseInt(b.number, 10);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.number.localeCompare(b.number);
    });
  }, [loadState]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredAthletes = useMemo<AthleteWithId[]>(() => {
    let list = sortedAthletes;
    if (!isSearching && activeFilter !== 'all') {
      list = list.filter((a) => a.position === activeFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.number.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedAthletes, activeFilter, searchQuery, isSearching]);

  const quickAccessAthletes = useMemo<AthleteWithId[]>(() => {
    const tabbedIds = coach?.tabbedAthletes;
    if (!tabbedIds || tabbedIds.length === 0) return [];
    const byId = new Map(sortedAthletes.map((a) => [a.id, a]));
    return tabbedIds
      .map((id) => byId.get(id))
      .filter((athlete): athlete is AthleteWithId => Boolean(athlete));
  }, [coach?.tabbedAthletes, sortedAthletes]);
  const quickAccessAthleteIdSet = useMemo(
    () => new Set(quickAccessAthletes.map((athlete) => athlete.id)),
    [quickAccessAthletes],
  );
  const hasQuickAccessHotbar = quickAccessAthletes.length > 0;

  /** Tryout-configured positions, stable order, trimmed, no duplicates. */
  const tryoutPositionFilters = useMemo(() => {
    if (loadState.status !== 'ready') return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of loadState.data.athletePositions) {
      const p = typeof raw === 'string' ? raw.trim() : '';
      if (p.length > 0 && !seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
    return out;
  }, [loadState]);

  const filterOptions = useMemo<Array<{ label: string; value: FilterValue }>>(
    () => [
      { label: 'All', value: 'all' },
      ...tryoutPositionFilters.map((p) => ({ label: p, value: p })),
    ],
    [tryoutPositionFilters],
  );

  useEffect(() => {
    if (loadState.status !== 'ready') return;
    if (activeFilter === 'all') return;
    if (!tryoutPositionFilters.includes(activeFilter)) {
      setActiveFilter('all');
    }
  }, [loadState.status, tryoutPositionFilters, activeFilter]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const header = (
    <PageTopNav
      onBack={() => navigate(`/tryout/${tryoutId}`)}
      backAriaLabel="Back to tryout dashboard"
      title={loadState.status === 'ready' ? loadState.data.name : ''}
    />
  );

  const renderScrollShell = (
    contentClassName: string,
    body: React.ReactNode,
  ): React.ReactElement => (
    <div className="tryout-athletes-shell">
      {header}
      <div
        ref={contentScrollRef}
        onScroll={handleContentScroll}
        className={contentClassName}
      >
        {body}
      </div>
      {hasQuickAccessHotbar ? (
        <div className="athlete-quick-select-hotbar">
          <AthleteQuickSelectTabBar
            athletes={quickAccessAthletes}
            currentAthleteId=""
            onAthleteSelect={(athleteId) => navigate(`/tryout/${tryoutId}/athletes/${athleteId}`)}
          />
        </div>
      ) : null}
      <ScrollToTopFab
        visible={scrollTopFabVisible}
        onPress={scrollContentToTop}
      />
    </div>
  );

  if (loadState.status === 'loading') {
    return renderScrollShell(
      'tryout-athletes-content',
      <>
        <div className="tryout-athletes-skeleton tryout-athletes-skeleton--search" />
        <div className="tryout-athletes-skeleton-filters">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="tryout-athletes-skeleton tryout-athletes-skeleton--chip" />
          ))}
        </div>
        <div className="tryout-athletes-list">
          {Array(10).fill(0).map((_, i) => (
            <div key={i} className="tryout-athletes-skeleton tryout-athletes-skeleton--card" />
          ))}
        </div>
      </>,
    );
  }

  if (loadState.status === 'error') {
    return renderScrollShell(
      'tryout-athletes-content tryout-athletes-content--centered',
      <>
        <p className="tryout-athletes-error">{loadState.message}</p>
        <Button variant="secondary" onClick={() => navigate(`/tryout/${tryoutId}`)}>
          Back to Dashboard
        </Button>
      </>,
    );
  }

  const { data } = loadState;

  return renderScrollShell(
    `tryout-athletes-content${hasQuickAccessHotbar ? ' tryout-athletes-content--with-hotbar' : ''}`,
    <>
        <AthleteSearchBar value={searchQuery} onChange={setSearchQuery} />

        {/* Filter chips — hidden while searching */}
        {!isSearching ? <div
          className="tryout-athletes-filter-row"
          role="group"
          aria-label="Filter by position"
        >
          {filterOptions.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              className={[
                'tryout-athletes-filter-chip',
                activeFilter === value ? 'tryout-athletes-filter-chip--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setActiveFilter(value)}
              aria-pressed={activeFilter === value}
            >
              {label}
            </button>
          ))}
        </div> : null}

        {/* Athlete list */}
        {filteredAthletes.length === 0 ? (
          <div className="tryout-athletes-empty">
            {data.athletes.length === 0 ? (
              <>
                <p className="tryout-athletes-empty-title">No athletes yet</p>
                <p className="tryout-athletes-empty-hint">
                  Tap "Add Athlete" on the dashboard to get started.
                </p>
              </>
            ) : (
              <p className="tryout-athletes-empty-title">
                No athletes match your search.
              </p>
            )}
          </div>
        ) : (
          <ul className="tryout-athletes-list" aria-label="Athletes">
            {filteredAthletes.map((athlete) => (
              <li key={athlete.id}>
                <AthleteCard
                  id={athlete.id}
                  name={athlete.name}
                  number={athlete.number}
                  position={athlete.position}
                  returningstatus={athlete.returningstatus}
                  isQuickAccess={quickAccessAthleteIdSet.has(athlete.id)}
                  imageThumbnailUrl={athlete.imageThumbnailUrl}
                  onClick={(id) => navigate(`/tryout/${tryoutId}/athletes/${id}`)}
                />
              </li>
            ))}
          </ul>
        )}
    </>,
  );
};

export default TryoutAthletesPage;
